// formik components
import { Form, Formik } from "formik";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// NewUser layout schemas for form and form feilds
import validations from "./schemas/validations";
import form from "./schemas/form";
import initialValues from "./schemas/initialValues";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import MDTypography from "components/MDTypography";
import { useRedirectForm } from "hooks/useRedirectForm";
import {
  useCerrarCicloMutation,
  useEditCicloMutation,
  useLazyGetCicloByIdQuery,
} from "services";
import CicloEdit from "./components/Ciclo/ciclo.edit";
import { handleConfirmDialog } from "helpers/handleToast";
import { HOME_URL } from "../../../../config/bases_url";

import { calificaciones } from "slices/Calificaciones/calificaciones.slice";
import { CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

function EditCiclo() {
  const { cicloSeleccionado } = useSelector(calificaciones);
  const { enqueueSnackbar } = useSnackbar();

  if (!cicloSeleccionado)
    return (
      <MDBox
        mt={5}
        width={"100%"}
        display={"flex"}
        py={5}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <CircularProgress sx={{ marginBottom: "3rem" }} color="inherit" />
      </MDBox>
    );

  useEffect(() => {
    if (!cicloSeleccionado.activo) {
      enqueueSnackbar(
        "Solo puede editar el ciclo actual, los históricos son de solo lectura, seleccione el ciclo activo primero.",
        {
          variant: "warning",
          autoHideDuration: 8000,
        }
      );
      navigate(HOME_URL);
    }
  }, [cicloSeleccionado]);

  const [
    editCiclo,
    {
      isSuccess: isSuccessE,
      isLoading: isLoadingE,
      isError: isErrorE,
      error: errorE,
    },
  ] = useEditCicloMutation();

  const [
    cerrarCiclo,
    {
      isSuccess: isSuccessCC,
      isLoading: isLoadingCC,
      isError: isErrorCC,
      error: errorCC,
    },
  ] = useCerrarCicloMutation();

  const { formId, formField } = form;
  const currentValidation = validations[0];

  const navigate = useNavigate();

  const [getCicloById] = useLazyGetCicloByIdQuery();

  useRedirectForm(
    isLoadingE,
    isSuccessE,
    isErrorE,
    errorE,
    "Ciclo editado",
    "/ciclos/ciclos-list"
  );

  useRedirectForm(
    isLoadingCC,
    isSuccessCC,
    isErrorCC,
    errorCC,
    "Ciclo cerrado exitosamente",
    HOME_URL
  );

  const submitForm = async (values, actions) => {
    await editCiclo(values);

    if (isSuccessE) {
      actions.setSubmitting(false);
      actions.resetForm(); // Notify parent to refresh ciclos
    }
  };

  const handleSubmit = (values, actions) => {
    handleConfirmDialog({
      title: "¿Estás seguro que deseas subir los cambios?",
      dispatcher: async (result) => {
        if (result.isConfirmed) {
          await submitForm(values, actions);
        }
      },
    });
  };

  const handleCerrarCiclo = () => {
    handleConfirmDialog({
      title: "¿Estás seguro?",
      text:
        "¡Al aceptar esta accion se cerrará por completo el ciclo actual y no sera reversible!",
      confirmButtonText: "Terminar",
      cancelButtonText: "Cancelar",
      dispatcher: (result) => {
        if (result.isConfirmed) {
          cerrarCiclo();
        }
      },
    });
  };

  return (
    <MDBox mt={5} mb={20}>
      <Card
        sx={{
          width: 470,
          minWidth: 470,
          mx: "auto",
          borderRadius: "20px",
        }}
      >
        <Grid container justifyContent="center" alignItems="center">
          <Grid item xs={12} lg={12}>
            <MDBox mt={3} textAlign="center">
              <MDBox mb={1}>
                <MDTypography variant="h5" fontWeight="bold">
                  Año Escolar
                </MDTypography>
              </MDBox>
            </MDBox>
            <Formik
              initialValues={initialValues}
              validationSchema={currentValidation}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, setFieldValue }) => {
                useEffect(() => {
                  if (cicloSeleccionado?.id) {
                    getCicloById({ id: cicloSeleccionado?.id })
                      .unwrap()
                      .then((res) => {
                        setFieldValue(formField.nombre.name, res?.nombre, true);
                        setFieldValue(
                          formField.fecha_fin.name,
                          res?.fecha_fin
                            ? res?.fecha_fin
                            : initialValues.fecha_fin,
                          true
                        );
                        setFieldValue(
                          formField.fecha_inicio.name,
                          res?.fecha_inicio
                            ? res?.fecha_inicio
                            : initialValues.fecha_inicio,
                          true
                        );
                      });
                  }
                }, [cicloSeleccionado]);

                return (
                  <Form id={formId} autoComplete="off">
                    <MDBox p={4} pt={0}>
                      <MDBox>
                        <CicloEdit
                          formData={{
                            values,
                            touched,
                            errors,
                            formField,
                            setFieldValue,
                          }}
                        />

                        <MDBox
                          mt={2}
                          width="100%"
                          display="flex"
                          justifyContent="center"
                        >
                          <MDButton
                            onClick={handleCerrarCiclo}
                            variant="gradient"
                            size={"large"}
                            color="warning"
                          >
                            cerrar ciclo actual
                          </MDButton>
                        </MDBox>
                        <MDBox
                          mt={2}
                          width="100%"
                          display="flex"
                          justifyContent="space-between"
                        >
                          <MDButton
                            onClick={() => navigate(`/ciclos/ciclos-list`)}
                            variant="outlined"
                            color="accent"
                          >
                            Cancelar
                          </MDButton>
                          <MDButton
                            type="submit"
                            variant="contained"
                            color="accent"
                          >
                            Aceptar
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Form>
                );
              }}
            </Formik>
          </Grid>
        </Grid>
      </Card>
    </MDBox>
  );
}

export default EditCiclo;
